/**
 * PortfolioView Component
 * Single Responsibility: Display portfolio information (canvas widget; relocated
 * out of the deleted LiveCockpit tree by TASK-665).
 */

import React, { useState, useEffect } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { portfolioApiService } from '../../services/api/portfolioApiService'
import ExecutiveSummary from './ExecutiveSummary'
import PriorityActions from './PriorityActions'
import CompositionTab from './CompositionTab'
import PerformanceTab from './PerformanceTab'
import RiskTab from './RiskTab'
import HistoryTab from './HistoryTab'
import { debugLog } from '../../utils/debug'

interface PortfolioViewProps {
  clientId: string
}

const PortfolioView: React.FC<PortfolioViewProps> = ({ clientId }) => {
  const [activeTab, setActiveTab] = useState<'composition' | 'performance' | 'risk' | 'history'>('composition')
  const [showPositions, setShowPositions] = useState(false)
  const [portfolioData, setPortfolioData] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchPortfolioData = async () => {
      try {
        setLoading(true)
        const data = await portfolioApiService.getClientDashboardData(clientId)
        setPortfolioData(data)
      } catch (error) {
        debugLog('ERROR', 'Failed to fetch portfolio data', error)
        setPortfolioData(null)
      } finally {
        setLoading(false)
      }
    }

    fetchPortfolioData()
  }, [clientId])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading portfolio data...</p>
        </div>
      </div>
    )
  }

  if (!portfolioData) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <p className="text-red-600">Failed to load portfolio data. Please try again.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Portfolio Health & Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Portfolio Overview</h3>
          <p className="text-sm text-gray-600">Real-time portfolio health and metrics</p>
        </div>
        <ExecutiveSummary
          mode="client"
          data={portfolioData?.overview}
          loading={loading}
        />
      </div>

      {/* Priority Actions */}
      <PriorityActions
        mode="client"
        clientId={clientId}
        allocation={portfolioData?.allocation}
        risk={portfolioData?.risk}
        stressTests={portfolioData?.stressTests}
        performance={portfolioData?.performance}
        cash={portfolioData?.totalCashCHF}
        portfolioValue={portfolioData?.overview?.total_value}
        onAction={(action, data) => {
          debugLog('USER_ACTION', 'Portfolio action triggered', { action, data })
        }}
        loading={loading}
      />

      {/* Portfolio Deep Dive Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200">
          {(['composition', 'performance', 'risk', 'history'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`
                flex-1 px-4 py-3 text-sm font-medium capitalize transition-colors
                ${activeTab === tab
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }
              `}
            >
              {tab}
              {/* TASK-877: read "(Hidden)" on a tab label and the natural reading is that
                  the TAB is hidden — which it plainly is not, since it is on screen. The
                  badge reports `showPositions`, and it renders on the Composition tab even
                  while another tab is active, so it has to name its own subject. */}
              {tab === 'composition' && !showPositions && (
                <span className="ml-2 text-xs text-gray-500">Positions hidden</span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'composition' && (
            <div>
              {/* Position Toggle */}
              <div className="flex justify-end mb-4">
                <button
                  onClick={() => setShowPositions(!showPositions)}
                  className={`
                    px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-all
                    ${showPositions
                      ? 'bg-blue-600 text-white shadow-sm hover:bg-blue-700'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }
                  `}
                >
                  {showPositions ? (
                    <>
                      <Eye className="w-4 h-4" />
                      <span>Hide Positions</span>
                    </>
                  ) : (
                    <>
                      <EyeOff className="w-4 h-4" />
                      <span>Show Positions</span>
                    </>
                  )}
                </button>
              </div>
              <CompositionTab
                positions={portfolioData?.positions || []}
                allocation={portfolioData?.allocation}
                cashPositions={portfolioData?.cash || []}
                totalCashCHF={portfolioData?.totalCashCHF}
                portfolioValue={portfolioData?.overview?.total_value || 0}
                loading={loading}
                hidePositions={!showPositions}
                clientId={clientId}
              />
            </div>
          )}

          {activeTab === 'performance' && (
            <PerformanceTab
              mode="client"
              metrics={portfolioData?.performance}
              history={portfolioData?.performanceHistory}
              attribution={portfolioData?.performanceAttribution}
              rollingReturns={portfolioData?.rollingReturns}
              loading={loading}
              clientId={clientId}
            />
          )}

          {activeTab === 'risk' && (
            <RiskTab
              mode="client"
              riskMetrics={portfolioData?.risk}
              stressTests={portfolioData?.stressTests}
              correlationMatrix={portfolioData?.correlationMatrix}
              varDistribution={portfolioData?.varDistribution}
              positions={portfolioData?.positions}
              portfolioValue={portfolioData?.overview?.total_value}
              loading={loading}
            />
          )}

          {activeTab === 'history' && (
            <HistoryTab
              clientId={clientId}
              transactions={portfolioData?.transactions || []}
              loading={loading}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default PortfolioView